import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import confetti from "canvas-confetti";
import { Ticket, X, ShieldCheck, AlertCircle, Loader2, CheckCircle2 } from "lucide-react";
import popSound from './sounds/popo.mp3';
import "./subscriptionModal.css";

const SubscriptionModal = ({ course, onClose }) => {
  const [subscriptionCode, setSubscriptionCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const fireCelebration = () => {
    const duration = 3 * 1000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#3b82f6', '#8b5cf6', '#10b981']
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#3b82f6', '#8b5cf6', '#10b981']
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
    new Audio(popSound).play().catch(e => console.log("Audio blocked"));
  };

  const handleSubmit = async () => {
    if (!subscriptionCode) {
      setError("من فضلك أدخل كود الاشتراك");
      return;
    }

    setLoading(true);
    setError(null);

    const id = localStorage.getItem("id");
    const lesson_id = localStorage.getItem("package_id");
    const deviceFingerprint = localStorage.getItem("deviceFingerprint");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/codes/validate`,
        {
          code: subscriptionCode,
          user_id: id,
          lesson_id: lesson_id,
          mac_address: deviceFingerprint,
        }
      );

      if (response.data.message === "Code validated and updated successfully.") {
        setSuccess(true);
        localStorage.setItem("subscriptionCode", subscriptionCode);
        fireCelebration();

        // إعادة تحميل الصفحة بعد 5 ثواني
        setTimeout(() => {
          window.location.reload();
        }, 5000);
      }
    } catch (err) {
      const msg = err.response?.data?.message;
      setError(msg === "Code is already used." ? "هذا الكود مستخدم بالفعل" : "الكود غير صحيح، يرجى المحاولة مرة أخرى");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <AnimatePresence>
        <motion.div
          className="modal-card"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          {/* زر إغلاق مميز واحترافي */}
          <button className="close-floating-btn" onClick={onClose} aria-label="Close">
            X
          </button>

          {!success ? (
            <div className="modal-body">
              <div className="header-art">
                <div className="blob-bg"></div>
                <Ticket size={40} className="floating-icon" />
              </div>

              <h2 className="modal-title">تفعيل الكورس</h2>
              <p className="modal-subtitle">ادخل كود الاشتراك المكون من أرقام وحروف لفتح المحتوى</p>

              <div className={`modern-input-group ${error ? 'input-error-state' : ''}`}>
                <ShieldCheck className="inner-input-icon" size={20} />
                <input
                  type="text"
                  value={subscriptionCode}
                  onChange={(e) => setSubscriptionCode(e.target.value)}
                  placeholder="XXX-XXX-XXX"
                  autoFocus
                />
              </div>

              {error && (
                <motion.div className="error-alert" initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}>
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </motion.div>
              )}

              <button
                className={`premium-btn ${loading ? 'btn-loading' : ''}`}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? <Loader2 className="spin-icon" /> : "تأكيد الاشتراك الآن"}
              </button>
            </div>
          ) : (
            <motion.div className="success-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="success-icon-wrapper">
                <CheckCircle2 size={80} className="draw-check" />
              </div>
              <h3 className="success-title">تهانينا، تم التفعيل!</h3>
              <p className="success-text">سيتم تحديث الصفحة لبدء المشاهدة خلال 5 ثوانٍ...</p>
              <div className="progress-bar-container">
                <motion.div 
                  className="progress-fill"
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 5 }}
                />
              </div>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default SubscriptionModal;